import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { Star, StarFill } from '@gravity-ui/icons';
import { Button, Flex, Icon, Text, useToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { getSkillReviews } from 'client/api';
import { withLines } from 'client/blocks/ReviewPage/RatingPage';
import { PageLayout } from 'client/blocks-redesign/Layout/PageLayout';
import { PageLoader } from 'client/blocks-redesign/Loader/PageLoader';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { moment } from 'client/lib/moment';
import { UserReview } from 'client/model/reviews';
import * as generalKeyset from 'i18n/GeneralSettings';
import * as keyset from 'i18n/UserRatings';

import styles from './AliceAppRatingPage.module.scss';

const RATING_PAGE_TOAST = 'RATING_PAGE_TOAST';

const t = i18n(keyset);
const tGeneralSettings = i18n(generalKeyset);

const Stars: FC<{ rating: number }> = ({ rating }) => (
    <Flex gap={1} alignItems="center">
        {Array.from(Array(5).keys(), (x) => x + 1).map((x) => {
            const isActive = rating > 0 && rating >= x;

            return (
                <Icon
                    key={x}
                    className={clx(isActive ? styles.ratingPage__star_active : styles.ratingPage__star_idle)}
                    data={isActive ? StarFill : Star}
                    size={16}
                />
            );
        })}
    </Flex>
);

export const AliceAppRatingPage = () => {
    const context = useContext(CurrentAliceAppContext);
    const [items, setItems] = useState<UserReview[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(true);
    const [page, setPage] = useState(0);

    const { add: addToast } = useToaster();

    const loadMore = useCallback(
        async (hasMore: boolean, page: number) => {
            if (!context?.aliceApp || !hasMore) {
                return;
            }

            try {
                setIsLoading(true);
                const newPage = page + 1;
                const result = await getSkillReviews(context.aliceApp.id, context.aliceApp.channel, newPage);
                setItems((prev) => [...prev, ...result.items]);
                setPage(newPage);
                setHasMore(result.hasMore);
            } catch (error) {
                console.error(error);
                addToast({ title: tGeneralSettings('error'), theme: 'danger', name: RATING_PAGE_TOAST });
            } finally {
                setIsLoading(false);
            }
        },
        [context?.aliceApp, addToast],
    );

    useEffect(() => {
        if (context?.aliceApp) {
            loadMore(true, 0);
        }
    }, [context?.aliceApp, loadMore]);

    if (!context?.aliceApp) {
        return <PageLoader />;
    }

    const skill = context.aliceApp;

    if (page === 0 && isLoading) {
        return (
            <PageLayout aliceApp={skill} pageTitle={t('header')}>
                <PageLoader />
            </PageLayout>
        );
    }

    return (
        <PageLayout aliceApp={skill} pageTitle={t('header')}>
            {items && items.length ? (
                <>
                    <table className={styles.ratingPage__table}>
                        <thead>
                            <tr>
                                <th className={styles.ratingPage__ratingHeader}>{t('table-rating')}</th>
                                <th className={styles.ratingPage__dateHeader}>{t('table-date')}</th>
                                <th className={styles.ratingPage__reviewTextHeader}>{t('table-feedback')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(({ rating, reviewText, createdAt }, i) => (
                                <tr key={i} className={styles.ratingPage__review}>
                                    <td className={styles.ratingPage__rating}>
                                        <Stars rating={rating} />
                                    </td>
                                    <td className={styles.ratingPage__date}>
                                        {moment(createdAt).format('DD.MM.YYYY')}
                                        <br />
                                        {moment(createdAt).format('HH:mm')}
                                    </td>
                                    <td className={styles.ratingPage__reviewText}>{withLines(reviewText)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {hasMore && (
                        <Flex justifyContent="center" className={styles.ratingPage__loadMore}>
                            <Button size="l" onClick={() => loadMore(hasMore, page)} loading={isLoading}>
                                {t('download-more')}
                            </Button>
                        </Flex>
                    )}
                </>
            ) : (
                <div>
                    <Text>{t('no-feedback')}</Text>
                </div>
            )}
        </PageLayout>
    );
};
